/**
 * Lastausgleich.
 *
 * Die Seite ist so gebaut, dass die Reihenfolge des Aufbaus sichtbar bleibt:
 * Gerät, Pool, Mitglieder, Verträge. Jeder Schritt hat seinen eigenen Ort, denn
 * jeder kann für sich schiefgehen – und wer nicht weiß, an welchem Schritt es
 * hängt, sucht an der falschen Stelle.
 *
 * Zwei Dinge stehen bewusst prominent statt versteckt in einem Untermenü:
 *
 *  - **Der Abzug** (`drain`). Er ist der eigentliche Grund, warum man
 *    Lastausgleich betreibt: Erst er erlaubt es, einen Knoten zu warten, zu
 *    räumen oder zu verkaufen, ohne dass ein Kunde etwas merkt.
 *  - **Die gemessene Verfügbarkeit.** Sie steht neben jedem Pool, weil sie das
 *    Versprechen ist, für das der Aufbau bezahlt wurde. Ist sie nicht bei
 *    100 %, will man wissen, warum – nicht erst am Monatsende in der
 *    SLA-Gutschrift.
 */
import { useMemo, useState } from 'react';
import type { BackendPool, ContractId, LoadBalancer, ServerId } from '@hosting-sim/core';
import { ALGORITHM_LABEL, BalanceAlgorithms, poolCandidates, serverCapacity } from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
} from '../components/ui';
import { SellDeviceButton } from '../components/SellDevice';
import { BuyHardwareDialog, DeviceStatus, useCatalog } from './Infrastructure';
import { useGame } from '../store/game';
import { bytes, dec, mbps, num, pct } from '../lib/format';

export function LoadBalancers() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const catalog = useCatalog();
  const [buying, setBuying] = useState(false);
  const [creatingOn, setCreatingOn] = useState<LoadBalancer | null>(null);

  const balancers = useMemo(() => (world ? Object.values(world.loadBalancers) : []), [world]);
  if (!world) return null;

  const pools = balancers.flatMap((lb) => lb.pools);
  const pooledContracts = pools.reduce((sum, pool) => sum + pool.contractIds.length, 0);
  const worstAvailability = pools.filter((p) => p.contractIds.length > 0).reduce((min, p) => Math.min(min, p.availability), 1);

  return (
    <>
      <PageHeader
        title="Loadbalancer"
        description="Virtuelle Adressen, Backend-Pools und Health-Checks: Verfügbarkeit, die nicht am einzelnen Server hängt."
        actions={
          <Button variant="primary" onClick={() => setBuying(true)}>
            Loadbalancer kaufen
          </Button>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Geräte" value={num(balancers.length)} hint={`${balancers.filter((b) => b.peerId).length} im Paar`} />
        <Stat label="Pools" value={num(pools.length)} hint={`${pools.reduce((s, p) => s + p.members.length, 0)} Mitglieder`} />
        <Stat label="Verträge über Pools" value={num(pooledContracts)} hint="überleben den Ausfall eines Knotens" />
        <Stat
          label="Schlechteste Poolverfügbarkeit"
          value={pools.length > 0 ? pct(worstAvailability, 2) : '–'}
          tone={worstAvailability < 0.999 ? 'warning' : 'positive'}
          hint="in der letzten Stunde gemessen"
        />
      </StatGrid>

      {balancers.length === 0 ? (
        <Card className="mt-4">
          <EmptyState
            title="Noch kein Loadbalancer im Bestand"
            description={
              'Ohne Lastausgleich liegt jeder Kunde auf genau einem Server – dessen Ausfall ihn eine Stunde offline nimmt. ' +
              'Ein Pool aus zwei Knoten macht daraus die paar Sekunden, die der Health-Check braucht. Der Preis dafür ist ' +
              'ehrlich: Jedes Mitglied trägt die volle Last des Pools, zwei Knoten Redundanz heißen zwei Knoten Hardware.'
            }
            action={
              <Button variant="primary" onClick={() => setBuying(true)}>
                Loadbalancer kaufen
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="mt-4 space-y-4">
          {balancers.map((balancer) => (
            <BalancerCard key={balancer.id} balancer={balancer} onCreatePool={() => setCreatingOn(balancer)} />
          ))}
        </div>
      )}

      <BuyHardwareDialog
        open={buying}
        onClose={() => setBuying(false)}
        kind="loadbalancer"
        catalog={catalog}
        onBuy={async (rackId, modelKey) => {
          const result = await run({ type: 'hw/buyLoadBalancer', rackId: rackId as never, modelKey });
          return result.ok;
        }}
      />

      {creatingOn && <CreatePoolDialog balancer={creatingOn} onClose={() => setCreatingOn(null)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Ein Gerät mit seinen Pools
// ---------------------------------------------------------------------------

function BalancerCard({ balancer, onCreatePool }: { balancer: LoadBalancer; onCreatePool: () => void }) {
  const world = useGame((s) => s.world)!;
  const run = useGame((s) => s.run);

  const carrying = balancer.pools.reduce((sum, pool) => sum + pool.contractIds.length, 0);
  const partner = balancer.peerId ? world.loadBalancers[balancer.peerId] : null;
  const partnerOptions = Object.values(world.loadBalancers).filter(
    (lb) => lb.id !== balancer.id && !lb.peerId && lb.datacenterId === balancer.datacenterId && lb.pools.length === 0,
  );

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          {balancer.name}
          {balancer.peerId && (
            <Badge tone={balancer.haRole === 'active' ? 'success' : 'muted'}>{balancer.haRole === 'active' ? 'aktiv' : 'Reserve'}</Badge>
          )}
        </span>
      }
      subtitle={`${mbps(balancer.throughputMbps)} · ${num(balancer.connectionCapacity)} Verbindungen · ${num(balancer.tlsHandshakesPerSecond)} TLS-Handshakes/s · ${world.datacenters[balancer.datacenterId]?.city}`}
      actions={
        <>
          <DeviceStatus device={balancer} />
          <Button size="sm" onClick={onCreatePool}>
            Pool anlegen
          </Button>
          <SellDeviceButton kind="loadbalancer" id={balancer.id} name={balancer.name} />
        </>
      }
    >
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Auslastung">
          <span className={balancer.load > 0.85 ? 'text-red-600 dark:text-red-400' : ''}>{pct(balancer.load, 0)}</span>
        </Field>
        <Field label="Verkehr">{mbps(balancer.pools.reduce((s, p) => s + p.trafficMbps, 0))}</Field>
        <Field label="Verträge">{num(carrying)}</Field>
        <Field label="Partner">{partner ? partner.name : 'keiner'}</Field>
      </div>

      <BalancerUplink balancer={balancer} />

      {/*
        Der Balancer allein im Pfad ist der klassische Fehlschluss: hinten
        redundant, vorne nicht. Der Hinweis steht deshalb nicht in der Diagnose
        allein, sondern hier, wo man ihn beheben kann.
      */}
      {carrying > 0 && !balancer.peerId && (
        <Notice tone="warning" title="Dieses Gerät steht allein im Pfad">
          <p>
            Hinter ihm sind die Server redundant, vor ihm ist es niemand. Sein Ausfall nimmt alle {carrying} Verträge mit.
          </p>
          {partnerOptions.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs">Mit einem zweiten Gerät koppeln:</span>
              {partnerOptions.map((option) => (
                <Button
                  key={option.id}
                  size="sm"
                  onClick={() => void run({ type: 'lb/pair', loadBalancerId: balancer.id, peerId: option.id })}
                >
                  {option.name}
                </Button>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs">
              Dafür braucht es ein zweites Gerät am selben Standort, das noch keine eigenen Pools trägt.
            </p>
          )}
        </Notice>
      )}

      {partner && (
        <div className="mb-3">
          <Button size="sm" variant="ghost" onClick={() => void run({ type: 'lb/unpair', loadBalancerId: balancer.id })}>
            Kopplung mit {partner.name} aufheben
          </Button>
        </div>
      )}

      {balancer.pools.length === 0 ? (
        <p className="text-xs text-panel-500 dark:text-panel-400">
          Noch kein Pool angelegt. Ein Balancer ohne Pool ist ein Gerät, das Strom zieht.
        </p>
      ) : (
        <div className="space-y-3">
          {balancer.pools.map((pool) => (
            <PoolPanel key={pool.id} balancer={balancer} pool={pool} />
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Netzanbindung des Balancers.
 *
 * Ein Gerät, das die virtuelle Adresse hält, aber an keinem Port hängt, nimmt
 * den ganzen Pool vom Netz – unabhängig davon, wie viele Server dahinterstehen.
 * Deshalb steht die Verkabelung hier direkt an der Karte und nicht in einer
 * Detailansicht, die man erst öffnen muss.
 */
function BalancerUplink({ balancer }: { balancer: LoadBalancer }) {
  const world = useGame((s) => s.world)!;
  const run = useGame((s) => s.run);
  const [target, setTarget] = useState('');

  const current = useMemo(() => {
    for (const sw of Object.values(world.switches)) {
      const port = sw.ports.find((p) => p.connectedDevice?.kind === 'loadbalancer' && p.connectedDevice.id === balancer.id);
      if (port) return { sw, port };
    }
    return null;
  }, [world, balancer.id]);

  const options = useMemo(() => {
    const list: { value: string; label: string }[] = [];
    for (const sw of Object.values(world.switches)) {
      if (sw.datacenterId !== balancer.datacenterId) continue;
      for (const port of sw.ports) {
        if (port.connectedDevice || port.linkId) continue;
        list.push({
          value: `${sw.id}:${port.index}`,
          label: `${sw.name} Port ${port.index} · ${port.speedMbps >= 1000 ? `${port.speedMbps / 1000} G` : `${port.speedMbps} M`}`,
        });
      }
    }
    return list;
  }, [world, balancer.datacenterId]);

  if (current) {
    return (
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
        <Field label="Angeschlossen an">
          {current.sw.name} Port {current.port.index}
        </Field>
        <Button size="sm" variant="ghost" onClick={() => void run({ type: 'cable/disconnect', kind: 'loadbalancer', deviceId: balancer.id })}>
          Abziehen
        </Button>
      </div>
    );
  }

  return (
    <Notice tone="danger" title="Nicht verkabelt">
      <p>Solange der Balancer an keinem Switch hängt, ist alles offline, was er tragen soll.</p>
      {options.length === 0 ? (
        <p className="mt-1 text-xs">An diesem Standort ist kein Switch-Port frei.</p>
      ) : (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Select label="Freier Port" value={target} onChange={(e) => setTarget(e.target.value)} className="w-auto">
            <option value="">– bitte wählen –</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="primary"
            disabled={!target}
            onClick={() => {
              const [switchId, portIndex] = target.split(':');
              void run({
                type: 'cable/connect',
                kind: 'loadbalancer',
                deviceId: balancer.id,
                switchId: switchId as never,
                portIndex: Number(portIndex),
              });
            }}
          >
            Stecken
          </Button>
        </div>
      )}
    </Notice>
  );
}

// ---------------------------------------------------------------------------
// Ein Pool
// ---------------------------------------------------------------------------

function PoolPanel({ balancer, pool }: { balancer: LoadBalancer; pool: BackendPool }) {
  const world = useGame((s) => s.world)!;
  const run = useGame((s) => s.run);
  const [adding, setAdding] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const serving = pool.members.filter((m) => {
    const server = world.servers[m.serverId];
    return m.healthy && !m.drain && server && (server.state === 'online' || server.state === 'degraded');
  });

  return (
    <div className="rounded-lg border divider p-3">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium">
            {pool.name}
            {pool.vip && <Mono className="text-panel-500">{pool.vip}</Mono>}
            {serving.length === 0 && pool.contractIds.length > 0 && <Badge tone="danger">liefert nichts aus</Badge>}
            {serving.length === 1 && pool.contractIds.length > 0 && <Badge tone="warning">keine Redundanz</Badge>}
          </p>
          <p className="text-2xs text-panel-500 dark:text-panel-400">
            {ALGORITHM_LABEL[pool.algorithm]} · Prüfung alle {pool.healthCheckSeconds} s, {pool.unhealthyThreshold} Fehlversuche ·{' '}
            {pool.tlsTermination ? 'TLS am Balancer' : 'TLS auf den Backends'} ·{' '}
            {pool.sessionPersistence ? 'Sitzungsbindung an' : 'ohne Sitzungsbindung'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setAdding(true)}>
            Mitglied aufnehmen
          </Button>
          <Button size="sm" onClick={() => setAttaching(true)}>
            Verträge zuordnen
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void run({ type: 'lb/deletePool', poolId: pool.id })}
          >
            Löschen
          </Button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Field label="Verfügbarkeit (letzte Stunde)">
          <span className={pool.availability < 0.999 ? 'text-amber-600 dark:text-amber-400' : ''}>{pct(pool.availability, 3)}</span>
        </Field>
        <Field label="Verträge">{num(pool.contractIds.length)}</Field>
        <Field label="Ausliefernde Knoten">
          {serving.length} / {pool.members.length}
        </Field>
        <Field label="Verkehr">{mbps(pool.trafficMbps)}</Field>
      </div>

      {/* --- Mitglieder --- */}
      {pool.members.length === 0 ? (
        <p className="text-xs text-panel-500 dark:text-panel-400">Noch kein Mitglied – der Pool liefert nichts aus.</p>
      ) : (
        <ul className="space-y-1">
          {pool.members.map((member) => {
            const server = world.servers[member.serverId];
            const capacity = server ? serverCapacity(world, server.id) : null;
            return (
              <li key={member.serverId} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
                <StatusDot
                  tone={member.drain ? 'muted' : member.healthy ? 'success' : 'danger'}
                  label={server?.name ?? 'unbekannt'}
                />
                {capacity && (
                  <span className="text-panel-500 dark:text-panel-400">
                    {pct(capacity.ramGbUsed / Math.max(1, capacity.ramGb), 0)} von {bytes(capacity.ramGb)} belegt
                  </span>
                )}
                <span className="text-panel-500 dark:text-panel-400">Gewicht {member.weight}</span>
                {member.drain && <Badge tone="muted">im Abzug</Badge>}
                {!member.healthy && !member.drain && <Badge tone="danger">ausgefallen</Badge>}
                <span className="flex-1" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void run({ type: 'lb/setDrain', poolId: pool.id, serverId: member.serverId, drain: !member.drain })}
                >
                  {member.drain ? 'zurück in die Rotation' : 'in den Abzug'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void run({ type: 'lb/removeMember', poolId: pool.id, serverId: member.serverId })}
                >
                  entfernen
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {/* --- Einstellungen --- */}
      <div className="mt-3 grid gap-3 border-t divider pt-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Verteilverfahren"
          value={pool.algorithm}
          onChange={(e) => void run({ type: 'lb/updatePool', poolId: pool.id, patch: { algorithm: e.target.value as never } })}
        >
          {BalanceAlgorithms.map((algorithm) => (
            <option key={algorithm} value={algorithm}>
              {ALGORITHM_LABEL[algorithm]}
            </option>
          ))}
        </Select>
        <Input
          label="Prüfintervall (s)"
          type="number"
          min={1}
          max={300}
          defaultValue={pool.healthCheckSeconds}
          onBlur={(e) =>
            void run({ type: 'lb/updatePool', poolId: pool.id, patch: { healthCheckSeconds: Number(e.target.value) || 5 } })
          }
          hint={`Erkennungslücke ${pool.healthCheckSeconds * pool.unhealthyThreshold} s`}
        />
        <Input
          label="Fehlversuche bis Ausschluss"
          type="number"
          min={1}
          max={10}
          defaultValue={pool.unhealthyThreshold}
          onBlur={(e) =>
            void run({ type: 'lb/updatePool', poolId: pool.id, patch: { unhealthyThreshold: Number(e.target.value) || 3 } })
          }
          hint="niedriger heißt schneller, aber nervöser"
        />
        <div className="space-y-2 text-xs">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={pool.sessionPersistence}
              onChange={(e) => void run({ type: 'lb/updatePool', poolId: pool.id, patch: { sessionPersistence: e.target.checked } })}
              className="mt-0.5"
            />
            <span>
              <strong>Sitzungsbindung.</strong> Derselbe Besucher bleibt auf demselben Knoten. Ohne sie werden Kunden mitten im
              Arbeiten abgemeldet.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={pool.tlsTermination}
              onChange={(e) => void run({ type: 'lb/updatePool', poolId: pool.id, patch: { tlsTermination: e.target.checked } })}
              className="mt-0.5"
            />
            <span>
              <strong>TLS hier beenden.</strong> Nimmt den Backends die Handshakes ab und belastet dafür dieses Gerät –{' '}
              {num(balancer.tlsHandshakesPerSecond)} pro Sekunde sind die Grenze.
            </span>
          </label>
        </div>
      </div>

      {pool.contractIds.length > 0 && (
        <div className="mt-3 border-t divider pt-3">
          <Meter
            value={pool.availability}
            max={1}
            invert
            label={
              <>
                <span>Gemessene Verfügbarkeit</span>
                <span className="tabular">{pct(pool.availability, 3)}</span>
              </>
            }
          />
          <p className="mt-1 text-2xs text-panel-500 dark:text-panel-400">
            Fällt ein Mitglied aus, kostet das genau die Erkennungslücke von {pool.healthCheckSeconds * pool.unhealthyThreshold}{' '}
            Sekunden – und auch die nur für den Verkehrsanteil, der auf diesen Knoten fiel. Ohne Pool wäre es die ganze Stunde.
          </p>
        </div>
      )}

      {adding && <AddMemberDialog balancer={balancer} pool={pool} onClose={() => setAdding(false)} />}
      {attaching && <AttachContractsDialog pool={pool} onClose={() => setAttaching(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialoge
// ---------------------------------------------------------------------------

function CreatePoolDialog({ balancer, onClose }: { balancer: LoadBalancer; onClose: () => void }) {
  const run = useGame((s) => s.run);
  const world = useGame((s) => s.world)!;
  const [name, setName] = useState('web-pool');
  const [vip, setVip] = useState('');
  const [busy, setBusy] = useState(false);

  // Eigene Adressen als Vorschlag: eine virtuelle Adresse ist keine erfundene.
  const addresses = Object.values(world.prefixes)
    .filter((p) => p.family === 'ipv4' && (p.usage === 'customer' || p.usage === 'infrastructure' || p.usage === 'anycast'))
    .slice(0, 6);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Pool auf ${balancer.name} anlegen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!name.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await run({
                  type: 'lb/createPool',
                  loadBalancerId: balancer.id,
                  name: name.trim(),
                  vip: vip.trim() || null,
                });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Anlegen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} hint="z. B. web-pool, mail-pool" />
        <Input
          label="Virtuelle Adresse (optional)"
          value={vip}
          onChange={(e) => setVip(e.target.value)}
          hint="Die Adresse, unter der der Dienst nach außen erscheint. Kann später nachgetragen werden."
        />
        {addresses.length > 0 && (
          <p className="text-2xs text-panel-500 dark:text-panel-400">
            Eigene Netze:{' '}
            {addresses.map((p) => (
              <Mono key={p.id} className="mr-2">
                {p.network}/{p.length}
              </Mono>
            ))}
          </p>
        )}
        <Notice tone="info" title="Was als Nächstes kommt">
          Ein frischer Pool liefert nichts aus. Erst kommen Mitglieder hinein – mindestens zwei, sonst ist es keine Redundanz –,
          dann werden Verträge darauf gelegt. Jedes Mitglied muss die volle Last des Pools allein tragen können.
        </Notice>
      </div>
    </Modal>
  );
}

/** Gründe, warum ein Server (noch) nicht Mitglied werden kann – in Klartext. */
function memberBlockers(world: ReturnType<typeof useGame.getState>['world'] & object, balancer: LoadBalancer, pool: BackendPool, serverId: string): string[] {
  const s = world.servers[serverId as ServerId];
  if (!s) return ['nicht gefunden'];
  const out: string[] = [];
  if (s.datacenterId !== balancer.datacenterId) out.push('steht in einem anderen Standort');
  if (pool.members.some((m) => m.serverId === s.id)) out.push('ist schon Mitglied');
  if (s.os === null) out.push('kein Betriebssystem');
  if (s.retirement) out.push('zur Abgabe vorgemerkt');
  if (s.state !== 'online' && s.state !== 'degraded') out.push(`Zustand „${s.state}“`);
  if (s.uplink === null) out.push('nicht verkabelt');
  return out;
}

/**
 * Alle Server des Standorts mit dem Grund, warum sie nicht aufgenommen werden
 * können. Ein Server ohne Betriebssystem bekommt hier direkt einen Knopf zur
 * Installation, statt dass man die Seite wechseln muss.
 */
function ServerBlockers({ world, balancer, pool }: { world: NonNullable<ReturnType<typeof useGame.getState>['world']>; balancer: LoadBalancer; pool: BackendPool }) {
  const run = useGame((s) => s.run);
  const [busy, setBusy] = useState<string | null>(null);
  const servers = Object.values(world.servers).filter((s) => s.datacenterId === balancer.datacenterId);
  if (servers.length === 0) {
    return (
      <Notice tone="warning" title="Keine Server im Standort">
        In {world.datacenters[balancer.datacenterId]?.city} gibt es noch keinen Server. Erst im Rechenzentrum einbauen.
      </Notice>
    );
  }
  return (
    <div className="rounded border border-panel-200 dark:border-panel-700">
      <div className="border-b border-panel-200 px-3 py-2 text-xs font-medium dark:border-panel-700">Server im Standort</div>
      <ul className="divide-y divide-panel-200 text-sm dark:divide-panel-700">
        {servers.map((s) => {
          const reasons = memberBlockers(world, balancer, pool, s.id);
          return (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span>{s.name}</span>
              {reasons.length === 0 ? (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">aufnehmbar</span>
              ) : (
                <span className="flex items-center gap-2 text-xs text-panel-500 dark:text-panel-400">
                  {reasons.join(' · ')}
                  {s.os === null && (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy === s.id}
                      onClick={async () => {
                        setBusy(s.id);
                        try {
                          await run({ type: 'server/installOs', serverId: s.id, os: 'linux-debian' as never });
                        } finally {
                          setBusy(null);
                        }
                      }}
                    >
                      Betriebssystem installieren
                    </Button>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddMemberDialog({ balancer, pool, onClose }: { balancer: LoadBalancer; pool: BackendPool; onClose: () => void }) {
  const world = useGame((s) => s.world)!;
  const run = useGame((s) => s.run);
  const [serverId, setServerId] = useState<ServerId | ''>('');
  const [weight, setWeight] = useState('1');
  const [busy, setBusy] = useState(false);

  const candidates = useMemo(() => poolCandidates(world, pool.id), [world, pool.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mitglied in „${pool.name}“ aufnehmen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!serverId}
            onClick={async () => {
              if (!serverId) return;
              setBusy(true);
              try {
                const result = await run({ type: 'lb/addMember', poolId: pool.id, serverId, weight: Number(weight) || 1 });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Aufnehmen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {candidates.length === 0 ? (
          <Notice tone="warning" title="Kein geeigneter Server">
            In {world.datacenters[balancer.datacenterId]?.city} steht kein betriebsbereiter Server mit Betriebssystem zur
            Verfügung, der noch nicht Mitglied ist. Ein Pool über Standorte hinweg braucht Anycast, keinen Balancer.
          </Notice>
        ) : (
          <>
            <Select label="Server" value={serverId} onChange={(e) => setServerId(e.target.value as ServerId)}>
              <option value="">– bitte wählen –</option>
              {candidates.map((s) => {
                const capacity = serverCapacity(world, s.id);
                return (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.cpuCores} Kerne, {bytes(s.ramGb)}, {pct(capacity.ramGbUsed / Math.max(1, capacity.ramGb), 0)} belegt
                  </option>
                );
              })}
            </Select>
            <Input
              label="Gewicht"
              type="number"
              min={1}
              max={100}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              hint="Ein doppelt so starker Knoten trägt bei doppeltem Gewicht doppelt so viel."
            />
            <Notice tone="info" title="Was das an Kapazität kostet">
              Das neue Mitglied bekommt eine vollständige Kopie: Es trägt ab sofort den gesamten Ressourcenbedarf der{' '}
              {pool.contractIds.length} Pool-Verträge, nicht nur seinen Anteil. Genau das ist der Preis der Redundanz – und der
              Grund, warum es auch beim Ausfall der anderen weiterläuft.
            </Notice>
          </>
        )}
        <ServerBlockers world={world} balancer={balancer} pool={pool} />
      </div>
    </Modal>
  );
}

/**
 * Verträge auf einen Pool legen.
 *
 * Angeboten werden nur Verträge, die auf einem einzelnen Server liegen. Eine
 * VPS ist eine Maschine und lässt sich nicht replizieren; Colocation liefert
 * der Kunde selbst aus.
 */
function AttachContractsDialog({ pool, onClose }: { pool: BackendPool; onClose: () => void }) {
  const world = useGame((s) => s.world)!;
  const run = useGame((s) => s.run);
  const [busy, setBusy] = useState<ContractId | null>(null);

  const attachable = useMemo(
    () =>
      Object.values(world.contracts).filter(
        (c) => c.state === 'active' && c.serverId && !c.vpsId && !c.rackId && c.poolId !== pool.id,
      ),
    [world, pool.id],
  );
  const attached = useMemo(
    () => pool.contractIds.map((id) => world.contracts[id]).filter((c): c is NonNullable<typeof c> => !!c),
    [world, pool.contractIds],
  );

  const describe = (contractId: ContractId): string => {
    const contract = world.contracts[contractId];
    const product = contract ? world.products[contract.productId] : undefined;
    const customer = contract ? world.customers[contract.customerId] : undefined;
    return `${product?.name ?? 'Vertrag'} – ${customer?.company ?? customer?.name ?? 'Kunde'}`;
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={`Verträge auf „${pool.name}“`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4">
        {attached.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium">Liegt bereits auf diesem Pool ({attached.length})</p>
            <ul className="space-y-1">
              {attached.map((contract) => (
                <li key={contract.id} className="surface-muted flex items-center gap-2 p-2 text-xs">
                  <span className="min-w-0 flex-1 truncate">{describe(contract.id)}</span>
                  <Badge tone="success">redundant</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-medium">
            Kann aufgenommen werden{' '}
            <span className="font-normal text-panel-500 dark:text-panel-400">
              (nur Verträge, die auf einem einzelnen Server liegen)
            </span>
          </p>
          {attachable.length === 0 ? (
            <p className="text-xs text-panel-500 dark:text-panel-400">Es gibt derzeit keinen passenden Vertrag.</p>
          ) : (
            <ul className="space-y-1">
              {attachable.slice(0, 40).map((contract) => (
                <li key={contract.id} className="surface-muted flex items-center gap-2 p-2 text-xs">
                  <span className="min-w-0 flex-1 truncate">{describe(contract.id)}</span>
                  <span className="shrink-0 text-panel-500 dark:text-panel-400">
                    {contract.serverId ? world.servers[contract.serverId]?.name : '–'}
                  </span>
                  <Button
                    size="sm"
                    loading={busy === contract.id}
                    onClick={async () => {
                      setBusy(contract.id);
                      try {
                        await run({ type: 'lb/attachContract', poolId: pool.id, contractId: contract.id });
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    aufnehmen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Notice tone="info" title="Was sich damit ändert">
          Der Vertrag verliert seinen einzelnen Server und liegt auf der Gruppe. Der Ausfall eines Mitglieds kostet ihn dann
          nicht mehr eine Stunde, sondern die Erkennungslücke des Health-Checks – bei {pool.healthCheckSeconds} Sekunden
          Intervall und {pool.unhealthyThreshold} Fehlversuchen sind das {pool.healthCheckSeconds * pool.unhealthyThreshold}{' '}
          Sekunden – also {dec(((pool.healthCheckSeconds * pool.unhealthyThreshold) / 3600) * 100, 3)} % der Stunde.
        </Notice>
      </div>
    </Modal>
  );
}
